"use client";

import { PropsWithChildren, createContext, useContext } from "react";
import { UseFormReturn } from "react-hook-form";
import {
	LandingPageForm,
	useLandingPageForm,
} from "@/features/landing/use-landing-page-form";
import {
	AboutLoanForm,
	useAboutForm,
} from "@/features/purpose/use-about-loan-form";
import { EconomyForm, useEconomyForm } from "@/hooks/form/use-economy-form";
import {
	PersonalInformationForm,
	usePersonalInformationForm,
} from "@/hooks/form/use-personal-information-form";
import { useZodErrorMap } from "@/hooks/form/use-zod-error-map";
import { useAppCountry } from "@/hooks/use-app-country";

/* eslint-disable @typescript-eslint/no-explicit-any */

export type FormContextType = {
	aboutForm: UseFormReturn<AboutLoanForm, any, AboutLoanForm>;
	personalInformationForm: UseFormReturn<
		PersonalInformationForm,
		any,
		PersonalInformationForm
	>;
	economyForm: UseFormReturn<EconomyForm, any, EconomyForm>;
	landingPageForm: UseFormReturn<LandingPageForm, any, LandingPageForm>;
};

export const FormContext = createContext<FormContextType>(
	{} as FormContextType
);

/**
 * @returns the form assigned to the key
 */
export const useFormContext = <T extends keyof FormContextType>(key: T) => {
	return useContext(FormContext)[key];
};

const FormProviderInner = ({ children }: PropsWithChildren) => {
	useZodErrorMap();
	const value: FormContextType = {
		aboutForm: useAboutForm().useForm,
		personalInformationForm: usePersonalInformationForm().useForm,
		economyForm: useEconomyForm().useForm,
		landingPageForm: useLandingPageForm().useForm,
	};

	return (
		<FormContext.Provider {...{ value }}>{children}</FormContext.Provider>
	);
};

/** Schemas and defaults are read at mount, so a dev country switch remounts the forms. */
export const FormProvider = ({ children }: PropsWithChildren) => (
	<FormProviderInner key={useAppCountry()}>{children}</FormProviderInner>
);
